import React, { useState, useMemo } from 'react';
import './DetailedAnalysis.css';
import MetricCard from './MetricCard';
import { Checkbox } from './animate-ui/base/checkbox';
import { getTournamentType } from '../utils/dataProcessor';
import graphicNameMappings from '../assets/graphicNameMappings.json';

const DetailedAnalysis = ({ data, filters, onFilterChange }) => {
  const [selectedGfxType, setSelectedGfxType] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [showComparison, setShowComparison] = useState(false);

  const getGraphicDisplayName = (rawName) => {
    if (!rawName) return rawName;
    
    const normalizeKey = (str) => str.toLowerCase().trim().replace(/[\s\-_]+/g, ' ');
    const normalized = normalizeKey(rawName);
    
    for (const [key, value] of Object.entries(graphicNameMappings)) {
      if (normalizeKey(key) === normalized) {
        return value;
      }
    }
    
    return rawName;
  };

  // Get unique GFX types and years - moved to top before any early returns
  const gfxTypes = useMemo(() => {
    if (!data || data.length === 0) return [];
    const types = [...new Set(data.map(row => row['Graphic Type']).filter(Boolean))];
    return types.sort();
  }, [data]);

  const years = useMemo(() => {
    if (!data || data.length === 0) return [];
    const yearSet = [...new Set(data.map(row => row.Year).filter(Boolean))];
    return yearSet.sort();
  }, [data]);

  // Filter data based on selected GFX type and year - moved to top
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let filtered = data.filter(row => {
      const duration = typeof row.Duration_Seconds === 'number' ? row.Duration_Seconds : parseFloat(row.Duration_Seconds);
      return isNaN(duration) || duration <= 60;
    });

    if (selectedGfxType) {
      filtered = filtered.filter(row => row['Graphic Type'] === selectedGfxType);
    }

    if (selectedYear !== 'all') {
      filtered = filtered.filter(row => row.Year === selectedYear);
    }

    return filtered;
  }, [data, selectedGfxType, selectedYear]);

  // Calculate comprehensive metrics for the selected GFX type
  const calculateGfxMetrics = (data, year = null) => {
    if (!data || data.length === 0) return null;

    const filteredByYear = year ? data.filter(row => row.Year === year) : data;
    if (filteredByYear.length === 0) return null;

    // Group by tournament and event series
    const tournamentData = {};
    const dailyData = {};
    const eventSeriesData = { '250': [], '500': [], '1000': [], 'WTF': [], 'NextGen': [], 'Other': [] };

    filteredByYear.forEach(row => {
      const tournament = row.Tournament;
      const date = row.Date;
      const duration = row.Duration_Seconds || 0;
      const eventSeries = getTournamentType({ Tournament: tournament, EventId: row.EventId }) || 'Other';

      // Tournament data
      if (!tournamentData[tournament]) {
        tournamentData[tournament] = { count: 0, duration: 0, dates: new Set() };
      }
      tournamentData[tournament].count++;
      tournamentData[tournament].duration += duration;
      if (date) {
        tournamentData[tournament].dates.add(date);
      }

      // Daily data
      if (date) {
        if (!dailyData[date]) {
          dailyData[date] = { count: 0, duration: 0 };
        }
        dailyData[date].count++;
        dailyData[date].duration += duration;
      }

      // Event series data - ensure the key exists before pushing
      if (!eventSeriesData[eventSeries]) {
        eventSeriesData[eventSeries] = [];
      }
      eventSeriesData[eventSeries].push({ duration, date, tournament });
    });

    // Calculate metrics
    const totalCount = filteredByYear.length;
    const totalDuration = filteredByYear.reduce((sum, row) => sum + (row.Duration_Seconds || 0), 0);
    const uniqueTournaments = Object.keys(tournamentData).length;
    const uniqueDays = Object.keys(dailyData).length;

    // Debug logging
    console.log('📊 GFX Metrics Calculation:', {
      totalCount,
      totalDuration,
      uniqueTournaments,
      uniqueDays,
      '250Count': eventSeriesData['250']?.length || 0,
      '500Count': eventSeriesData['500']?.length || 0,
      '1000Count': eventSeriesData['1000']?.length || 0,
      'sampleRow': filteredByYear[0]
    });

    // Average time per tournament
    const avgTimePerTournament = uniqueTournaments > 0 ? totalDuration / uniqueTournaments : 0;

    // Count per series
    const count250 = eventSeriesData['250']?.length || 0;
    const count500 = eventSeriesData['500']?.length || 0;
    const count1000 = eventSeriesData['1000']?.length || 0;

    // Total duration per series
    const duration250 = count250 > 0 ? eventSeriesData['250'].reduce((sum, item) => sum + item.duration, 0) : 0;
    const duration500 = count500 > 0 ? eventSeriesData['500'].reduce((sum, item) => sum + item.duration, 0) : 0;
    const duration1000 = count1000 > 0 ? eventSeriesData['1000'].reduce((sum, item) => sum + item.duration, 0) : 0;

    // Unique days per series
    const days250 = count250 > 0 ? new Set(eventSeriesData['250'].map(item => item.date).filter(Boolean)).size : 0;
    const days500 = count500 > 0 ? new Set(eventSeriesData['500'].map(item => item.date).filter(Boolean)).size : 0;
    const days1000 = count1000 > 0 ? new Set(eventSeriesData['1000'].map(item => item.date).filter(Boolean)).size : 0;

    // Unique tournaments per series
    const tournaments250 = count250 > 0 ? new Set(eventSeriesData['250'].map(item => item.tournament).filter(Boolean)).size : 0;
    const tournaments500 = count500 > 0 ? new Set(eventSeriesData['500'].map(item => item.tournament).filter(Boolean)).size : 0;
    const tournaments1000 = count1000 > 0 ? new Set(eventSeriesData['1000'].map(item => item.tournament).filter(Boolean)).size : 0;

    // Average time per event series (per occurrence)
    const avgTimePer250 = count250 > 0 ? duration250 / count250 : 0;
    const avgTimePer500 = count500 > 0 ? duration500 / count500 : 0;
    const avgTimePer1000 = count1000 > 0 ? duration1000 / count1000 : 0;

    // Average count per day
    const avgCountPerDay = uniqueDays > 0 ? totalCount / uniqueDays : 0;

    // Average count per day by event series
    const avgCountPerDay250 = days250 > 0 ? count250 / days250 : 0;
    const avgCountPerDay500 = days500 > 0 ? count500 / days500 : 0;
    const avgCountPerDay1000 = days1000 > 0 ? count1000 / days1000 : 0;

    // Average on screen time
    const avgOnScreenTime = totalCount > 0 ? totalDuration / totalCount : 0;

    // Average time per tournament by event series
    const avgOnScreenTime250 = tournaments250 > 0 ? duration250 / tournaments250 : 0;
    const avgOnScreenTime500 = tournaments500 > 0 ? duration500 / tournaments500 : 0;
    const avgOnScreenTime1000 = tournaments1000 > 0 ? duration1000 / tournaments1000 : 0;

    // Average on screen time per day
    const avgOnScreenTimePerDay = uniqueDays > 0 ? totalDuration / uniqueDays : 0;

    // Average on screen time per day by event series
    const avgOnScreenTimePerDay250 = days250 > 0 ? duration250 / days250 : 0;
    const avgOnScreenTimePerDay500 = days500 > 0 ? duration500 / days500 : 0;
    const avgOnScreenTimePerDay1000 = days1000 > 0 ? duration1000 / days1000 : 0;

    return {
      totalCount,
      totalDuration,
      avgTimePerTournament,
      avgTimePer250,
      avgTimePer500,
      avgTimePer1000,
      avgCountPerDay,
      avgCountPerDay250,
      avgCountPerDay500,
      avgCountPerDay1000,
      avgOnScreenTime,
      avgOnScreenTime250,
      avgOnScreenTime500,
      avgOnScreenTime1000,
      avgOnScreenTimePerDay,
      avgOnScreenTimePerDay250,
      avgOnScreenTimePerDay500,
      avgOnScreenTimePerDay1000
    };
  };

  const currentMetrics = calculateGfxMetrics(filteredData);
  
  // Create comparison data filtered by GFX type and comparison year
  const comparisonData = useMemo(() => {
    if (!showComparison || selectedYear === 'all' || !data || data.length === 0) return [];
    
    let comparison = data.filter(row => row.Year === (selectedYear === '2024' ? '2025' : '2024'));
    
    if (selectedGfxType) {
      comparison = comparison.filter(row => row['Graphic Type'] === selectedGfxType);
    }
    
    return comparison;
  }, [data, selectedGfxType, selectedYear, showComparison]);
  
  const comparisonMetrics = showComparison && comparisonData.length > 0 
    ? calculateGfxMetrics(comparisonData)
    : null;

  if (!data || data.length === 0) {
    return <div className="no-data">No data available</div>;
  }

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const formatCount = (count) => {
    return count.toFixed(1);
  };

  return (
    <div className="detailed-analysis-container">
      <div className="detailed-analysis-content">
        {/* GFX Type Selection */}
        <div className="analysis-section">
          <div className="gfx-selection">
            <div className="selection-group">
              <label>Select GFX Type:</label>
              <select 
                value={selectedGfxType} 
                onChange={(e) => setSelectedGfxType(e.target.value)}
                className="gfx-select"
              >
                <option value="">All GFX Types</option>
                {gfxTypes.map(type => (
                  <option key={type} value={type}>{getGraphicDisplayName(type)}</option>
                ))}
              </select>
            </div>
            
            <div className="selection-group">
              <label>Select Year:</label>
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(e.target.value)}
                className="year-select"
              >
                <option value="all">All Years</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {selectedYear !== 'all' && (
              <div className="selection-group">
                <label>Show Comparison with {selectedYear === '2024' ? '2025' : '2024'}:</label>
                <div className="checkbox-container">
                  <Checkbox 
                    id="comparison" 
                    checked={showComparison} 
                    onCheckedChange={(checked) => setShowComparison(checked)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* GFX Metrics */}
        {currentMetrics && (
          <div className="analysis-section">
            <h3>GFX Metrics for: {selectedGfxType ? getGraphicDisplayName(selectedGfxType) : 'All GFX Types'}</h3>
            
            {/* Top Section */}
            <div className="metrics-section">
              <h4>Top Level Metrics</h4>
              <div className="gfx-metrics-grid">
                <MetricCard 
                  title="Total Count" 
                  value={currentMetrics.totalCount} 
                  comparisonValue={showComparison && comparisonMetrics ? comparisonMetrics.totalCount : null}
                />
                <MetricCard 
                  title="Total On-Screen Time" 
                  value={formatDuration(currentMetrics.totalDuration)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.totalDuration) : null}
                />
                <MetricCard 
                  title="Average Time per Tournament" 
                  value={formatDuration(currentMetrics.avgTimePerTournament)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgTimePerTournament) : null}
                />
                <MetricCard 
                  title="Average On-Screen Time per Day" 
                  value={formatDuration(currentMetrics.avgOnScreenTimePerDay)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgOnScreenTimePerDay) : null}
                />
                <MetricCard 
                  title="Average On-Screen Time" 
                  value={formatDuration(currentMetrics.avgOnScreenTime)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgOnScreenTime) : null}
                />
                <MetricCard 
                  title="Average Count per Day" 
                  value={formatCount(currentMetrics.avgCountPerDay)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatCount(comparisonMetrics.avgCountPerDay) : null}
                />
              </div>
            </div>

            {/* 250 Section */}
            <div className="metrics-section">
              <h4>250 Series Metrics</h4>
              <div className="gfx-metrics-grid">
                <MetricCard 
                  title="Average On-Screen Time" 
                  value={formatDuration(currentMetrics.avgTimePer250)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgTimePer250) : null}
                />
                <MetricCard 
                  title="Average Count per Day" 
                  value={formatCount(currentMetrics.avgCountPerDay250)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatCount(comparisonMetrics.avgCountPerDay250) : null}
                />
                <MetricCard 
                  title="Average Time per Tournament" 
                  value={formatDuration(currentMetrics.avgOnScreenTime250)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgOnScreenTime250) : null}
                />
                <MetricCard 
                  title="Average On-Screen Time per Day" 
                  value={formatDuration(currentMetrics.avgOnScreenTimePerDay250)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgOnScreenTimePerDay250) : null}
                />
              </div>
            </div>

            {/* 500 Section */}
            <div className="metrics-section">
              <h4>500 Series Metrics</h4>
              <div className="gfx-metrics-grid">
                <MetricCard 
                  title="Average On-Screen Time" 
                  value={formatDuration(currentMetrics.avgTimePer500)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgTimePer500) : null}
                />
                <MetricCard 
                  title="Average Count per Day" 
                  value={formatCount(currentMetrics.avgCountPerDay500)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatCount(comparisonMetrics.avgCountPerDay500) : null}
                />
                <MetricCard 
                  title="Average Time per Tournament" 
                  value={formatDuration(currentMetrics.avgOnScreenTime500)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgOnScreenTime500) : null}
                />
                <MetricCard 
                  title="Average On-Screen Time per Day" 
                  value={formatDuration(currentMetrics.avgOnScreenTimePerDay500)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgOnScreenTimePerDay500) : null}
                />
              </div>
            </div>

            {/* 1000 Section */}
            <div className="metrics-section">
              <h4>1000 Series Metrics</h4>
              <div className="gfx-metrics-grid">
                <MetricCard 
                  title="Average On-Screen Time" 
                  value={formatDuration(currentMetrics.avgTimePer1000)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgTimePer1000) : null}
                />
                <MetricCard 
                  title="Average Count per Day" 
                  value={formatCount(currentMetrics.avgCountPerDay1000)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatCount(comparisonMetrics.avgCountPerDay1000) : null}
                />
                <MetricCard 
                  title="Average Time per Tournament" 
                  value={formatDuration(currentMetrics.avgOnScreenTime1000)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgOnScreenTime1000) : null}
                />
                <MetricCard 
                  title="Average On-Screen Time per Day" 
                  value={formatDuration(currentMetrics.avgOnScreenTimePerDay1000)} 
                  comparisonValue={showComparison && comparisonMetrics ? formatDuration(comparisonMetrics.avgOnScreenTimePerDay1000) : null}
                />
              </div>
            </div>
          </div>
        )}

        {/* Comparison Metrics */}
        {showComparison && comparisonMetrics && (
          <div className="analysis-section">
            <h3>Comparison: {selectedYear === '2024' ? '2025' : '2024'} vs {selectedYear}</h3>
            <div className="comparison-metrics-grid">
              <div className="comparison-column">
                <h4>{selectedYear === '2024' ? '2025' : '2024'}</h4>
                <MetricCard 
                  title="Total Count" 
                  value={comparisonMetrics.totalCount} 
                />
                <MetricCard 
                  title="Total On-Screen Time" 
                  value={formatDuration(comparisonMetrics.totalDuration)} 
                />
                <MetricCard 
                  title="Average Time per Tournament" 
                  value={formatDuration(comparisonMetrics.avgTimePerTournament)} 
                />
                {/* Add more comparison metrics as needed */}
              </div>
              <div className="comparison-column">
                <h4>{selectedYear}</h4>
                <MetricCard 
                  title="Total Count" 
                  value={currentMetrics.totalCount} 
                />
                <MetricCard 
                  title="Total On-Screen Time" 
                  value={formatDuration(currentMetrics.totalDuration)} 
                />
                <MetricCard 
                  title="Average Time per Tournament" 
                  value={formatDuration(currentMetrics.avgTimePerTournament)} 
                />
                {/* Add more comparison metrics as needed */}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailedAnalysis; 